import { NgxSliderModule, Options } from '@angular-slider/ngx-slider';
import { DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '@app/services';
import { CurrencyModule } from '@app/directives';
import { ProjectV2 } from '@interfaces/app';
import { PiV2PurchaseConfirmComponent } from './pi-v2-purchase-confirm/pi-v2-purchase-confirm.component';
import { PipesModule } from '@app/pipes';
import { ToastrService } from 'ngx-toastr';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SingularService } from 'src/infra/analytics/singular/singular.service';
import { TikTokContentType, TikTokService } from 'src/infra/analytics/tiktok/tiktok.service';
import { catchError, EMPTY, take } from 'rxjs';

@Component({
  selector: 'mnf-pi-v2-purchase',
  standalone: true,
  imports: [CommonModule, NgxSliderModule, FormsModule, ReactiveFormsModule, PipesModule, FontAwesomeModule, MatTooltipModule, CurrencyModule],
  templateUrl: './pi-v2-purchase.component.html',
  styleUrl: './pi-v2-purchase.component.scss'
})
export class PiV2PurchaseComponent implements OnInit, OnChanges {

  capital = [
    { capitalization: 'Mensual', text: 'Cada mes', 'value': 12 },
    { capitalization: 'Bimestral', text: 'Cada 2 meses', 'value': 6 },
    { capitalization: 'Venta o en 5 meses', text: 'Después de la venta o en 5 meses', value: 6 },
    { capitalization: 'Semestral', text: 'Cada 6 meses', value: 2 },
    { capitalization: 'Anual', text: 'Cada año', value: 1 },
    { capitalization: 'Semanal', text: 'Cada semana', value: 52 },
    { capitalization: 'Diario', text: 'Cada día', value: 365 },
    { capitalization: 'Quincenal', text: 'Cada 15 días', value: 24 },
    { capitalization: 'Trimestral', text: 'Cada 3 meses', value: 4 },
  ];

  infoProject: ProjectV2;

  formPurchase: FormGroup;

  performanceEstimate: number = 0;
  repartsTotal: number = 0;
  repartEstimate: number = 0;
  totalSum: number = 0;
  soldTotalPerformance: number = 0;
  totalPerformance: number = 0;
  totalFreePerUser: number = 0;
  partsActual: number = 0;

  stepOne: number;
  stepTwo: number;
  stepThree: number;
  stepFour: number;

  showLimitParts: boolean = false;
  userWithoutPurchaseLimit = false;

  isDisabled: boolean = false;

  user: any;

  iconClose = faXmark;

  get initAmount() {
    return this.formPurchase.controls.amount;
  }

  constructor(
   private modalServ: MatDialog,
   private dialogRef: DialogRef<any>,
   private fb: FormBuilder,
   private userServ: UserService,
   private toastServ: ToastrService,
   private singularServ: SingularService,
   private readonly tiktokServ: TikTokService,
   private cd: ChangeDetectorRef
   ) {
    this.formPurchase = this.fb.group({
      amount: [1000, [Validators.max(1000)]]
    });
  }

  options: Options = {
    floor: 1000,
    ceil: 1000000,
    showSelectionBar: true,
    step: 1000,
    animate: false,
    translate: (value: number): string => {
      return "$" + value.toLocaleString('es-MX', { minimumFractionDigits: 0 });
    }
  };

  ngOnInit(): void {
    this.userServ.getUserInf().subscribe(res => {
      this.user = res;
      this.userWithoutPurchaseLimit = this.user?.noPurchaseLimit;
      if (this.infoProject?.pid) {
        const specificProject = res?.participations?.find(part => part.pid === this.infoProject?.pid);
        this.partsActual = specificProject?.amount || 0;
        const data = { purchaseLimit: !this.userWithoutPurchaseLimit, availableParts: this.infoProject.phase.free, devPartsLimitPerUser: this.infoProject.participationsMaxPerUser, userParts: this.partsActual };
        this.totalFreePerUser = this.getMaxParticipationsToBuy(data);
        this.updateSliderOptions();
        this.updateFormState();
      }
    });

    this.performanceEstimate = this.initAmount.value * this.infoProject?.performance / 100;
    if(this.infoProject?.state !== 'variable') {
      this.stepOne = this.initAmount.value * (this.infoProject?.performance) / 1000;
      this.stepTwo = ((this.stepOne) * this.extractNumber(this.infoProject?.intervalMonthsResidential || this.infoProject?.monthsEstimateResidential) / 12);
      this.stepThree = this.calculateTotalReparts(this.infoProject?.capitalization, this.extractNumber(this.infoProject?.intervalMonthsResidential || this.infoProject?.monthsEstimateResidential));
      this.stepFour = this.stepTwo / this.stepThree;
      this.soldTotalPerformance = this.infoProject?.soldPerformance
      ? (this.initAmount.value * (this.infoProject.soldPerformance) / 100) * (this.extractNumber(this.infoProject?.monthsMaxLimit.toString()) / 12)
      : 0;
      this.repartEstimate = this.initAmount.value * this.stepFour / 1000;
      this.totalPerformance = this.initAmount.value * this.stepTwo / 1000;

      this.totalSum = this.initAmount.value + this.totalPerformance;
    } else {
      this.stepOne = (this.initAmount.value * 12 ) / 1000;
      this.stepTwo = ((this.stepOne) * 5 / 12);
      this.stepFour = this.stepTwo / 1;
      this.totalPerformance = this.initAmount.value * this.stepTwo / 1000;
      this.repartEstimate = this.initAmount.value * this.stepFour / 1000;
      this.totalSum = this.initAmount.value + this.totalPerformance;
    }
  }

  formatName(name: string | undefined): string {
    if (!name) return "";
    const regex = /\s-\s\d+$/;
    if (regex.test(name)) {
      return name.replace(regex, "");
    }
    return name;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.totalFreePerUser && !changes.totalFreePerUser.firstChange) {
      this.updateSliderOptions();
      this.updateFormState();
    }
  }

  updateFormState(): void {
    if (this.isDisabled) {
      this.formPurchase.controls['amount'].disable();
    } else {
      this.formPurchase.controls['amount'].enable();
    }
  }

  updateSliderOptions() {
    this.options = {
      ...this.options,
      ceil: this.totalFreePerUser * 1000
    };
  }

  editValue(value: number) {
    if(value == this.totalFreePerUser) {
      this.toastServ.info('Máximo de participaciones alcanzado, comunícate con soporte para adquirir más.');
    }
    this.initAmount.setValue(value);
    this.editInput();
    this.cd.detectChanges();
  }

  editInput() {

    if(this.infoProject?.state !== 'variable') {
      this.performanceEstimate = this.initAmount.value * this.totalPerformance / 100;
      this.repartEstimate = this.initAmount.value * this.stepFour / 1000;
      this.totalPerformance = this.initAmount.value * this.stepTwo / 1000;
      this.totalSum = this.initAmount.value + this.totalPerformance;
      this.soldTotalPerformance = this.infoProject?.soldPerformance
      ? (this.initAmount.value * (this.infoProject.soldPerformance) / 100) * (this.extractNumber(this.infoProject?.monthsMaxLimit.toString()) / 12)
      : 0;
    } else {
      this.totalPerformance = this.initAmount.value * this.stepTwo / 1000;
      this.performanceEstimate = (this.initAmount.value * 5) / 100;
      this.totalSum = this.initAmount.value + this.totalPerformance;
    }
  }

  getTextByCapitalization(capitalization: string): string {
    const item = this.capital.find(item => item.capitalization === capitalization);
    return item ? item.text : '';
  }

  extractNumber(text: string): number {
    const results = text?.match(/\d+/g);
    if (results && results?.length > 0) {
      return Math.max(...results.map(num => parseInt(num, 10)));
    }
    throw new Error("No se encontró ningún número en el texto.");
  }

  calculateTotalReparts(period: string, value: number): number {
    switch (period.toLowerCase()) {
      case 'mensual':
        return value;
      case 'bimestral':
        return value / 2;
      case 'trimestal':
        return value / 3;
      case 'semestral':
        return value / 6;
      case 'al final del plazo':
        return 1;
      case 'venta o en 5 meses':
        return 1;
      default:
        return 1;
    }
  }

  getMaxParticipationsToBuy(
    data: {
      purchaseLimit: boolean,
      availableParts: number,
      devPartsLimitPerUser: number,
      userParts: number
    }
  ): number {
    let maxParts: number;

    if (!data.purchaseLimit) {
      maxParts = data.availableParts;
    } else {
      maxParts = data.devPartsLimitPerUser - data.userParts;
      if (maxParts > data.availableParts) {
        maxParts = data.availableParts;
      }
    }

    this.isDisabled = maxParts < 1;
    return maxParts < 1 ? 0 : maxParts;
  }

  next() {
    this.dialogRef.close();
    this.singularServ.sendEvent('__INICIO_DE_COMPRA_DE_PARTICIPACIONES__', { sng_attr_content_list: this.infoProject?.pid, sng_attr_item_price: this.initAmount.value });
    this.tiktokServ.initiateCheckout({
      contents: [{
        contentID: this.infoProject.pid,  // Asegúrate de siempre incluir un content_id válido
        contentType: TikTokContentType.PRODUCT,
        contentName: 'PROYECTO',
        quantity: this.initAmount.value / this.infoProject.phase.partValue,
        price: this.infoProject.phase.partValue
      }],
      value: this.initAmount.value
    }).pipe(
      take(1),
      catchError(err => {
          console.error('Error al registrar evento en TikTok', err);
          return EMPTY;
      })
    ).subscribe(() => console.log('Evento registrado correctamente'));

    this.modalServ.open(PiV2PurchaseConfirmComponent, {
      data: {
        image: (this.infoProject?.gallery?.length > 0 ? this.infoProject?.gallery[0] : null) || (this.infoProject?.newProject?.sectionImages?.images.length > 0 ? this.infoProject?.newProject?.sectionImages?.images[0] : null),
        name: this.infoProject?.name,
        pid: this.infoProject?.pid,
        amount: this.initAmount.value / 1000,
        partValue: this.infoProject?.phase?.partValue || 1000,
        totalInvest: this.initAmount.value,
        baseProject: this.infoProject?.baseProject,
        balanceFinal: this.user?.wallet?.balance - this.initAmount.value,
        balance: this.user?.wallet?.balance,
        partsProjectFree: this.infoProject?.phase?.free,
        totalFreePerUser: this.totalFreePerUser
      },
      width: '500px',
      maxWidth: '500px',
      panelClass: 'auto-height-dialog',
      disableClose: true
    });
  }

  close(): void {
    this.dialogRef.close()
  }

}
